/**
 * Issuer and holder signing in the browser.
 *
 * The API prepares each credential (POST /credentials/prepare) and returns
 * exactly what to sign. Two ways to sign, neither of which sends a private key
 * anywhere:
 *   - MetaMask: personal_sign over the readable message the API returns
 *     (cryptosuite ida-eip191-jcs-2026).
 *   - The DID's Ed25519 key, pasted by the issuer and held only in memory:
 *     Ed25519 over the hashData the API returns (cryptosuite eddsa-jcs-2022).
 */
import type { Signer } from 'ethers';
import { apiFetch } from '../api/client';
import { encodePathSegment } from './url';

const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function base58Encode(bytes: Uint8Array): string {
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;
  const digits: number[] = [];
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i];
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }
  return '1'.repeat(zeros) + digits.reverse().map((d) => B58[d]).join('');
}

export function base58Decode(s: string): Uint8Array {
  let zeros = 0;
  while (zeros < s.length && s[zeros] === '1') zeros++;
  const bytes: number[] = [];
  for (let i = zeros; i < s.length; i++) {
    const v = B58.indexOf(s[i]);
    if (v < 0) throw new Error('invalid base58');
    let carry = v;
    for (let j = 0; j < bytes.length; j++) {
      carry += bytes[j] * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  const out = new Uint8Array(zeros + bytes.length);
  bytes.reverse().forEach((b, i) => { out[zeros + i] = b; });
  return out;
}

function hexToBytes(hex: string): Uint8Array {
  const h = hex.replace(/^0x/, '');
  if (h.length % 2 !== 0 || /[^0-9a-fA-F]/.test(h)) throw new Error('invalid hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function b64Decode(s: string): Uint8Array {
  const b = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b + '='.repeat((4 - (b.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

function b64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Read a pasted Ed25519 private key: the hex or base64 shown when the DID was
 * created (32-byte seed, or 64 bytes seed||public key).
 */
export function parseEd25519PrivateKey(text: string): Uint8Array {
  const s = text.trim();
  let raw: Uint8Array;
  try {
    raw = /^(0x)?[0-9a-fA-F]+$/.test(s) ? hexToBytes(s) : b64Decode(s);
  } catch {
    throw new Error('The key must be the hex or base64 private key shown when the DID was created.');
  }
  if (raw.length === 64) raw = raw.slice(0, 32);
  if (raw.length !== 32) throw new Error(`An Ed25519 private key is 32 bytes; this one is ${raw.length}.`);
  return raw;
}

// PKCS#8 wrapper for a raw Ed25519 seed (RFC 8410).
const PKCS8_PREFIX = hexToBytes('302e020100300506032b657004220420');

/** An Ed25519 key held in memory for signing. */
export interface Ed25519Key {
  key: CryptoKey;
  publicKey: Uint8Array;
}

/** Import a seed for signing. The browser keeps the key non-extractable. */
export async function importEd25519Key(seed: Uint8Array): Promise<Ed25519Key> {
  const pkcs8 = new Uint8Array(PKCS8_PREFIX.length + seed.length);
  pkcs8.set(PKCS8_PREFIX, 0);
  pkcs8.set(seed, PKCS8_PREFIX.length);
  let extractable: CryptoKey;
  try {
    extractable = await crypto.subtle.importKey('pkcs8', pkcs8 as unknown as BufferSource, { name: 'Ed25519' }, true, ['sign']);
  } catch {
    throw new Error('This browser cannot sign with Ed25519 keys. Use a current Chrome, Edge, Firefox or Safari, or sign with MetaMask.');
  }
  const jwk = await crypto.subtle.exportKey('jwk', extractable);
  const publicKey = b64Decode(jwk.x ?? '');
  const key = await crypto.subtle.importKey('pkcs8', pkcs8 as unknown as BufferSource, { name: 'Ed25519' }, false, ['sign']);
  return { key, publicKey };
}

interface DIDDoc {
  id: string;
  verificationMethod?: { id: string; publicKeyMultibase?: string; publicKeyJwk?: { crv?: string; x?: string } }[];
  assertionMethod?: string[];
  authentication?: string[];
}

function absolute(did: string, ref: string): string {
  return ref.startsWith('#') ? did + ref : ref;
}

function vmPublicKey(vm: NonNullable<DIDDoc['verificationMethod']>[number]): Uint8Array | null {
  if (vm.publicKeyJwk?.crv === 'Ed25519' && vm.publicKeyJwk.x) return b64Decode(vm.publicKeyJwk.x);
  const mb = vm.publicKeyMultibase;
  if (!mb?.startsWith('z')) return null;
  try {
    const raw = base58Decode(mb.slice(1));
    if (raw.length === 32) return raw;
    if (raw.length === 34 && raw[0] === 0xed && raw[1] === 0x01) return raw.slice(2);
  } catch { /* not base58 */ }
  return null;
}

/**
 * Find which of the DID's keys a pasted private key is, under the given
 * relationship. Returns its verification method id, or throws with a
 * message an issuer can act on.
 */
export async function findKeyInDID(did: string, publicKey: Uint8Array, relationship: 'assertionMethod' | 'authentication'): Promise<string> {
  const res = await apiFetch<{ didDocument?: DIDDoc } & DIDDoc>(`/dids/${encodePathSegment(did)}`);
  const doc: DIDDoc = res.didDocument ?? res;
  const allowed = new Set((doc[relationship] ?? []).map((r) => absolute(did, r)));
  const want = b64UrlEncode(publicKey);
  for (const vm of doc.verificationMethod ?? []) {
    const pub = vmPublicKey(vm);
    if (pub && b64UrlEncode(pub) === want) {
      const id = absolute(did, vm.id);
      if (!allowed.has(id)) throw new Error(`That key (${id}) is no longer an ${relationship} key of ${did}; it was rotated out.`);
      return id;
    }
  }
  throw new Error(`That key does not belong to ${did}.`);
}

/** Sign the API's hashData with an Ed25519 key; returns the proofValue. */
export async function signHashData(key: Ed25519Key, hashDataHex: string): Promise<string> {
  const data = hexToBytes(hashDataHex);
  const sig = new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, key.key, data as unknown as BufferSource));
  return 'z' + base58Encode(sig);
}

/** How the issuer signs. */
export type IssuerSigner =
  | { kind: 'wallet'; signer: Signer }
  | { kind: 'key'; key: Ed25519Key; verificationMethod: string };

export interface Prepared {
  credentialId: string;
  hashData: string;
  message?: string;
  cryptosuite: string;
}

/** The proofValue for what the API prepared. */
export async function proofValueFor(prepared: Prepared, signer: IssuerSigner): Promise<string> {
  if (signer.kind === 'key') return signHashData(signer.key, prepared.hashData);
  if (!prepared.message) throw new Error('This DID signs with an Ed25519 key, not a wallet. Choose the Ed25519 key and paste it.');
  return signer.signer.signMessage(prepared.message);
}

export function signingMethod(signer: IssuerSigner, did: string): string {
  return signer.kind === 'key' ? signer.verificationMethod : `${did}#key-1`;
}

/**
 * Prepare, sign and complete one credential. `body` is the same body the
 * one-step issue endpoint took.
 */
export async function issueSignedCredential(body: Record<string, unknown>, signer: IssuerSigner, issuerDid: string): Promise<{ id: string }> {
  const prepared = await apiFetch<Prepared>('/credentials/prepare', {
    method: 'POST',
    body: JSON.stringify({ ...body, verificationMethod: signingMethod(signer, issuerDid) }),
  });
  return apiFetch<{ id: string }>('/credentials/issue', {
    method: 'POST',
    body: JSON.stringify({ credentialId: prepared.credentialId, proofValue: await proofValueFor(prepared, signer) }),
  });
}

/** A delegation to grant (see POST /delegations/prepare). */
export interface DelegationRequest {
  delegatorDid: string;
  delegateDid: string;
  scope: string[];
  constraints?: Record<string, unknown>;
  validUntil?: string;
  maxDepth?: number;
  parentDelegationId?: string;
  /** The bank instrument a UPI mandate (scope upi-pay) is bound to. */
  instrument?: { kind: string; ref: string; attestation: string };
}

/** Prepare, sign and submit a delegation as its delegator. */
export async function issueSignedDelegation(req: DelegationRequest, signer: IssuerSigner): Promise<{ id: string }> {
  const prepared = await apiFetch<Prepared>('/delegations/prepare', {
    method: 'POST',
    body: JSON.stringify({ ...req, verificationMethod: signingMethod(signer, req.delegatorDid) }),
  });
  return apiFetch<{ id: string }>('/delegations', {
    method: 'POST',
    body: JSON.stringify({ credentialId: prepared.credentialId, proofValue: await proofValueFor(prepared, signer) }),
  });
}

/** Wallet DIDs are did:adi:[chainId:]0x<address>. */
export function isWalletDID(did: string | undefined): boolean {
  return !!did && /^did:adi:(\d+:)?0x[0-9a-fA-F]{40}$/.test(did);
}
